import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Toaster, toast } from 'sonner';
import {
  LayoutDashboard,
  Heart,
  Store,
  Calendar,
  DollarSign,
  LogOut,
  TrendingUp,
  TrendingDown,
  Wallet,
  Eye,
  EyeOff,
  Settings,
  QrCode,
  Download,
  Users,
  ExternalLink,
  Mic2,
  Wheat
} from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import InfaqManager from '../components/admin/InfaqManager';
import UMKMManager from '../components/admin/UMKMManager';
import EventManager from '../components/admin/EventManager';
import FinanceManager from '../components/admin/FinanceManager';
import SettingsManager from '../components/admin/SettingsManager';
import PengurusManager from '../components/admin/PengurusManager';
import ImamKhatibManager from '../components/admin/ImamKhatibManager';
import ZakatRamadanManager from '../components/admin/ZakatRamadanManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { settings: appSettings } = useSettings();
  const [financialSummary, setFinancialSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [mosqueData, setMosqueData] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalInfaq: 0,
    totalUMKM: 0,
    totalEvents: 0,
    infaqPrograms: []
  });
  const [settings, setSettings] = useState({
    show_financial_widget: false,
    widget_title: 'Transparansi Keuangan'
  });
  const [updatingSettings, setUpdatingSettings] = useState(false);

  useEffect(() => {
    fetchFinancialSummary();
    if (activeTab === 'overview') {
      fetchAnalytics();
      fetchSettings();
    }
  }, [activeTab]);

  useEffect(() => {
    if (user?.mosque_id) {
      axios.get(`${API_URL}/api/mosques/${user.mosque_id}`, { withCredentials: true })
        .then(r => setMosqueData(r.data))
        .catch(() => {});
    }
  }, [user?.mosque_id]);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings`, { withCredentials: true, params: { mosque_id: user?.mosque_id } });
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const toggleFinancialWidget = async () => {
    setUpdatingSettings(true);
    try {
      const newSettings = {
        ...settings,
        show_financial_widget: !settings.show_financial_widget
      };
      await axios.put(`${API_URL}/api/settings`, newSettings, { withCredentials: true });
      setSettings(newSettings);
      toast.success(
        newSettings.show_financial_widget
          ? 'Widget keuangan diaktifkan!'
          : 'Widget keuangan dinonaktifkan!'
      );
    } catch (error) {
      toast.error('Gagal mengupdate settings');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const [infaqRes, umkmRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/infaq`, { withCredentials: true, params: { mosque_id: user?.mosque_id } }),
        axios.get(`${API_URL}/api/umkm`, { withCredentials: true, params: { mosque_id: user?.mosque_id } }),
        axios.get(`${API_URL}/api/events`, { withCredentials: true, params: { mosque_id: user?.mosque_id } })
      ]);
      
      setAnalytics({
        totalInfaq: infaqRes.data.length,
        totalUMKM: umkmRes.data.length,
        totalEvents: eventsRes.data.length,
        infaqPrograms: infaqRes.data.slice(0, 5) // Top 5 programs
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/finance/summary`, {
        withCredentials: true,
        params: { mosque_id: user?.mosque_id }
      });
      setFinancialSummary(data);
    } catch (error) {
      console.error('Error fetching financial summary:', error);
      setFinancialSummary({
        total_kas: 125000000,
        monthly_income: 45000000,
        monthly_expense: 23000000,
        month: new Date().toISOString().slice(0, 7)
      });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const menuItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview', path: '/admin' },
    { id: 'infaq', icon: Heart, label: 'Program Infaq', path: '/admin/infaq' },
    { id: 'zakat-ramadan', icon: Wheat, label: 'Zakat Ramadan', path: '/admin/zakat-ramadan' },
    { id: 'umkm', icon: Store, label: 'UMKM', path: '/admin/umkm' },
    { id: 'events', icon: Calendar, label: 'Warta & Event', path: '/admin/events' },
    { id: 'finance', icon: DollarSign, label: 'Keuangan', path: '/admin/finance' },
    { id: 'pengurus', icon: Users, label: 'Pengurus', path: '/admin/pengurus' },
    { id: 'imam-khatib', icon: Mic2, label: 'Imam & Khatib', path: '/admin/imam-khatib' },
    { id: 'settings', icon: Settings, label: 'Pengaturan', path: '/admin/settings' }
  ];

  const mosqueFrontendUrl = mosqueData
    ? `${window.location.origin}/region/${mosqueData.regency_slug}/${mosqueData.village_slug}/${mosqueData.slug}`
    : null;

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-primary-dark">
                Admin Dashboard
              </h1>
              <p className="text-sm font-body text-text-secondary">
                {appSettings.mosque_name}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-body font-medium text-text-primary">{user?.name}</p>
                <p className="text-xs font-body text-text-secondary">{user?.email}</p>
              </div>
              {mosqueFrontendUrl && (
                <a
                  href={mosqueFrontendUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="admin-view-frontend-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-full font-body font-medium text-sm transition-colors border border-emerald-200"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="hidden sm:inline">Halaman Masjid</span>
                </a>
              )}
              <button
                data-testid="admin-logout-btn"
                onClick={handleLogout}
                className="p-2 rounded-full hover:bg-stone-100 transition-colors"
              >
                <LogOut className="w-5 h-5 text-stone-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-2xl border border-stone-200 p-2 mb-8 flex gap-2 overflow-x-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                data-testid={`admin-tab-${item.id}`}
                onClick={() => setActiveTab(item.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-body font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === item.id
                    ? 'bg-emerald-700 text-white'
                    : 'text-stone-600 hover:bg-stone-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Overview Content */}
        {activeTab === 'overview' && financialSummary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Widget Toggle Card */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    {settings.show_financial_widget ? (
                      <Eye className="w-7 h-7 text-white" />
                    ) : (
                      <EyeOff className="w-7 h-7 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold mb-1">
                      Widget Transparansi Keuangan
                    </h3>
                    <p className="text-white/80 text-sm font-body">
                      {settings.show_financial_widget
                        ? 'Widget ditampilkan di halaman Home publik'
                        : 'Widget disembunyikan dari halaman publik'}
                    </p>
                  </div>
                </div>
                <button
                  data-testid="toggle-financial-widget"
                  onClick={toggleFinancialWidget}
                  disabled={updatingSettings}
                  className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-emerald-700 ${
                    settings.show_financial_widget ? 'bg-white' : 'bg-white/30'
                  } ${updatingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-9 w-9 transform rounded-full transition-transform ${
                      settings.show_financial_widget
                        ? 'translate-x-12 bg-emerald-600'
                        : 'translate-x-1 bg-white'
                    }`}
                  />
                  <span className="sr-only">Toggle widget</span>
                </button>
              </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-body text-text-secondary">Program Infaq</p>
                    <p className="text-3xl font-body font-bold text-purple-700">
                      {analytics.totalInfaq}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                    <Store className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-body text-text-secondary">Toko UMKM</p>
                    <p className="text-3xl font-body font-bold text-amber-700">
                      {analytics.totalUMKM}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-body text-text-secondary">Event Aktif</p>
                    <p className="text-3xl font-body font-bold text-blue-700">
                      {analytics.totalEvents}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-sm font-body text-text-secondary">Total Kas</p>
                    <p className="text-2xl font-body font-bold text-emerald-800">
                      {formatRupiah(financialSummary.total_kas)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-body text-text-secondary">Pemasukan Bulan Ini</p>
                    <p className="text-2xl font-body font-bold text-green-700">
                      {formatRupiah(financialSummary.monthly_income)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-body text-text-secondary">Pengeluaran Bulan Ini</p>
                    <p className="text-2xl font-body font-bold text-red-700">
                      {formatRupiah(financialSummary.monthly_expense)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Infaq Programs Chart */}
            {analytics.infaqPrograms.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h3 className="text-lg font-heading font-semibold text-primary-dark mb-6">
                  Progress Program Infaq
                </h3>
                <div className="space-y-4">
                  {analytics.infaqPrograms.map((program) => {
                    const progress = (program.current_amount / program.target_amount) * 100;
                    return (
                      <div key={program.id}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-body font-medium text-text-primary">
                            {program.title}
                          </span>
                          <span className="text-sm font-body font-semibold text-emerald-700">
                            {progress.toFixed(0)}%
                          </span>
                        </div>
                        <div className="h-3 rounded-full bg-stone-100 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs font-body text-text-secondary">
                            {formatRupiah(program.current_amount)}
                          </span>
                          <span className="text-xs font-body text-text-secondary">
                            Target: {formatRupiah(program.target_amount)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Transparency Board */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h2 className="text-xl font-heading font-semibold text-primary-dark mb-6">
                Transparansi Keuangan
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-stone-100">
                  <span className="font-body text-text-secondary">Periode</span>
                  <span className="font-body font-semibold text-text-primary">
                    {new Date(financialSummary.month).toLocaleDateString('id-ID', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-stone-100">
                  <span className="font-body text-text-secondary">Total Pemasukan</span>
                  <span className="font-body font-bold text-green-700">
                    {formatRupiah(financialSummary.monthly_income)}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-stone-100">
                  <span className="font-body text-text-secondary">Total Pengeluaran</span>
                  <span className="font-body font-bold text-red-700">
                    {formatRupiah(financialSummary.monthly_expense)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="font-body font-semibold text-text-primary">Saldo Akhir</span>
                  <span className="font-body font-bold text-2xl text-emerald-800">
                    {formatRupiah(financialSummary.total_kas)}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h3 className="text-lg font-body font-semibold text-text-primary mb-4">
                Quick Actions
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {menuItems.slice(1).map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      data-testid={`quick-action-${item.id}`}
                      onClick={() => setActiveTab(item.id)}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border border-stone-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
                    >
                      <Icon className="w-6 h-6 text-emerald-700" />
                      <span className="text-sm font-body font-medium text-text-primary text-center">
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* QR Code Aplikasi Masjid */}
            {mosqueData && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="mosque-qr-card">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                    <QrCode className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-primary-dark">QR Code Aplikasi Masjid</h3>
                    <p className="text-sm text-stone-500">Tempel di papan masjid agar jamaah bisa install aplikasi</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="bg-white border-2 border-emerald-100 rounded-2xl p-3 shadow-sm flex-shrink-0">
                    <img
                      src={`${API_URL}/api/mosques/${user.mosque_id}/app-qr?site_url=${encodeURIComponent(window.location.origin)}`}
                      alt="QR Code Masjid"
                      className="w-36 h-36"
                      data-testid="mosque-qr-image"
                    />
                  </div>
                  <div className="flex-1 space-y-3 text-center sm:text-left">
                    <div>
                      <p className="text-xs text-stone-500 mb-1">Link Aplikasi Masjid:</p>
                      <p className="text-xs font-mono bg-stone-50 rounded-lg px-3 py-2 text-stone-700 break-all">
                        {window.location.origin}/region/{mosqueData.regency_slug}/{mosqueData.village_slug}/{mosqueData.slug}
                      </p>
                    </div>
                    <a
                      href={`${API_URL}/api/mosques/${user.mosque_id}/app-qr?site_url=${encodeURIComponent(window.location.origin)}`}
                      download="qr-masjid.png"
                      data-testid="mosque-qr-download-btn"
                      className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-body font-medium px-5 py-2.5 rounded-full transition-colors text-sm"
                    >
                      <Download className="w-4 h-4" /> Download QR Code
                    </a>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Placeholder for other tabs */}
        {activeTab === 'infaq' && <InfaqManager />}
        {activeTab === 'zakat-ramadan' && <ZakatRamadanManager />}
        {activeTab === 'umkm' && <UMKMManager />}
        {activeTab === 'events' && <EventManager />}
        {activeTab === 'finance' && <FinanceManager />}
        {activeTab === 'pengurus' && <PengurusManager />}
        {activeTab === 'imam-khatib' && <ImamKhatibManager />}
        {activeTab === 'settings' && <SettingsManager />}
      </div>
    </div>
  );
};

export default AdminDashboard;